import { ClassType, EntityType } from '@frontend/common';
import { StatusBadge, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { NotificationExtendedClient } from '@frontend/notification/api';
import { NotificationExtended, NotificationStatus } from '@frontend/notification/types';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { DefaultApplicationContainer } from '../../default-application-container/default-application-container';
import OverviewHeader from '../../header/overview-header';
import { useNotifications } from './notifications.controller';

export const Notifications = () => {
    const viewProps = useNotifications();
    const { openPlatformDetail } = usePlatformModalHost();

    return (
        <DefaultApplicationContainer>
            <OverviewHeader title='Notifications' />
            <ReactiveObjectList<NotificationExtended>
                id='notifications-list'
                useStaticListId
                dispatch={viewProps.dispatch}
                onSelectElement={(obj) =>
                    openPlatformDetail({
                        entityType: EntityType.NOTIFICATION,
                        id: obj.id,
                        parents: { account_id: obj.account_id }
                    })
                }
                fetch={(arg) => NotificationExtendedClient.fetchNotificationsExtended(arg)}
                activeFilter={{
                    searchKeys: ['subject', 'channel', 'user', 'tracking_number'],
                    filterKeys: ['notification_channel'],
                    queryKeyMap: new Map([['notification_channel', 'channel']]),
                    ...(viewProps.report && {
                        export: {
                            modalId: viewProps.reportModalId,
                            report: viewProps.report,
                            data: viewProps.exportData
                        }
                    })
                }}
                columns={{
                    columns: ['channel', 'language', 'subject', 'user', 'transaction', 'status', 'creation_timestamp'],
                    headerOverwrite: new Map([['transaction', CommonMessage.OBJECTS.TRANSACTION.DEFAULT]]),
                    customCells: new Map([
                        [
                            'user',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    {obj.row.original.user ? (
                                        <PlatformObjectBadge
                                            entityType={obj.row.original.user.entity_type}
                                            id={obj.row.original.user.entity_id}
                                            display={obj.row.original.user.name}
                                            key={obj.row.original.user.entity_id}
                                            foreign={obj.row.original.user.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.user)}
                                        />
                                    ) : (
                                        <span>/</span>
                                    )}
                                </td>
                            )
                        ],
                        [
                            'transaction',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    {obj.row.original.transaction ? (
                                        <PlatformObjectBadge
                                            id={obj.row.original.transaction.entity_id}
                                            entityType={EntityType.TRANSACTION}
                                            display={obj.row.original.transaction.name}
                                            key={obj.row.original.transaction.entity_id}
                                            foreign={obj.row.original.transaction.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.transaction)}
                                        />
                                    ) : (
                                        <span>/</span>
                                    )}
                                </td>
                            )
                        ],
                        [
                            'status',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <StatusBadge
                                        value={obj.row.original.status}
                                        colorMap={
                                            new Map([
                                                [[NotificationStatus.FAILED], ClassType.DANGER],
                                                [[NotificationStatus.PENDING], ClassType.INFO],
                                                [[NotificationStatus.SKIPPED], ClassType.DISABLED],
                                                [[NotificationStatus.SUCCEEDED], ClassType.SUCCESS]
                                            ])
                                        }
                                    />
                                </td>
                            )
                        ]
                    ])
                }}
                type={EntityType.NOTIFICATION}
            />
        </DefaultApplicationContainer>
    );
};
